import {
  FloatingPortal,
  useDismiss,
  useFloating,
  useInteractions,
} from "@floating-ui/react";
import React, { useEffect } from "react";

import { useFloatingTransition } from "../../hooks/useFloatingTransition";
import dimensionTokens from "../../tokens/dimension.value.tokens.json";
import ToastUi, { type ToastUiProps } from "./ToastUi";

const TOAST_OFFSET_VALUE = parseInt(dimensionTokens.spacing["lg"].$value);
const DURATION = 4000;

interface ToastProps {
  /** Toast variant */
  variant?: ToastUiProps["variant"];
  /** Toast content */
  children: React.ReactNode;
  /** Toast icon */
  icon?: ToastUiProps["icon"];
  /** Whether the toast is visible */
  open: boolean;
  /** Callback when toast should be dismissed */
  onOpenChange: (open: boolean) => void;
  /** Additional CSS classes */
  className?: string;
  /** Duration in milliseconds (0 to disable auto-dismiss) */
  duration?: number;
  /** ARIA live region politeness for screen readers */
  ariaLive?: "polite" | "assertive";
}

/**
 * Toast component that displays notifications at the top center of the screen.
 * Uses floating-ui for transitions and interactions.
 */
const Toast: React.FC<ToastProps> = ({
  variant = "neutral",
  children,
  icon,
  open,
  onOpenChange,
  className,
  duration = DURATION,
  ariaLive = variant === "alert" ? "assertive" : "polite",
}) => {
  // Use floating-ui context for transitions and interactions only
  const { refs, context } = useFloating({
    open,
    onOpenChange,
  });

  // Interaction hooks for dismissal
  const dismiss = useDismiss(context, {
    outsidePress: false, // Don't dismiss on outside press for toasts
    escapeKey: true, // Allow ESC key to dismiss
  });
  const { getFloatingProps } = useInteractions([dismiss]);

  // Transition hooks for fade in/out
  const { isMounted, transitionStyles } = useFloatingTransition(context);

  // Auto-dismiss functionality
  useEffect(() => {
    if (open && duration > 0) {
      const timer = setTimeout(() => {
        onOpenChange(false);
      }, duration);

      return () => clearTimeout(timer);
    }
  }, [open, duration, onOpenChange]);

  // Fixed position styles for top center
  const fixedStyles: React.CSSProperties = {
    position: "fixed",
    top: `${TOAST_OFFSET_VALUE}px`,
    left: "50%",
    transform: "translateX(-50%)",
    transformOrigin: "left", // Hack to ensure scale transforms from the center
    zIndex: 9999,
  };

  return (
    <FloatingPortal>
      {isMounted && (
        <div
          role={variant === "alert" ? "alert" : "status"}
          aria-live={ariaLive}
          aria-atomic="true"
          ref={refs.setFloating}
          style={{
            ...fixedStyles,
            ...transitionStyles,
          }}
          {...getFloatingProps()}
        >
          <ToastUi variant={variant} icon={icon} className={className}>
            {children}
          </ToastUi>
        </div>
      )}
    </FloatingPortal>
  );
};

export default Toast;
export type { ToastProps };
